import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { ActivityMemoAction, ActivityMemoGetter } from '@/store/activityMemo/activityMemo.module';
import { ActivityMemoSchema } from '@/store/activityMemo/activityMemo.validator';
import { ActivityMemo } from '@/store/activityMemo/activityMemo.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ActivityMemoFormComponent',
  components: {
    BaseModalComponent
  },
  emits: [
    'submited'
  ],
  props: {
    activityId: {
      type: Number,
    },
    provinceId: {
      type: Number,
    },
    activityMemoId: {
      type: Number,
    }
  },
  setup: (props, context) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => props.activityMemoId != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขกิจกรรม' : 'เพิ่มกิจกรรม');
    const activityMemo = computed<ActivityMemo | undefined>(() => store.getters[ActivityMemoGetter.BY_ID](props.activityMemoId));
    const activityMemoForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      activityId?: number | null;
      provinceId?: number | null;
      id?: number | null;
      subject?: string | null;
    }
    const formData = reactive<Form>({
      activityId: null,
      provinceId: null,
      id: null,
      subject: '',
    });
    const validator = computed(() => ActivityMemoSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      subject: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'subject')),
    });
    const isValid = computed(() => !errors.subject);
    const isLoading = computed<boolean>(() => store.getters[ActivityMemoGetter.STATUS] === 'init' || store.getters[ActivityMemoGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.activityId = props.activityId;
        formData.provinceId = props.provinceId;

        if (isEditMode.value) {
          formData.id = props.activityMemoId;
          formData.subject = activityMemo.value?.subject;
        }
        resolve();
      })
      .then(() => activityMemoForm.value?.show());
    };

    // hide modal
    const hide = () => {
      activityMemoForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.activityId = null;
        formData.provinceId = null;
        formData.id = null;
        formData.subject = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        const action = (isEditMode.value)
          ? ActivityMemoAction.UPDATE
          : ActivityMemoAction.CREATE;

        store.dispatch(action, formData)
          .then(() => {
            context.emit('submited');
            hide();
          });
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(ActivityMemoAction.DELETE, formData)
              .then(() => {
                context.emit('submited');
                hide();
              });
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      activityMemoForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  },
});
